import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useLocalStorage } from "usehooks-ts";

import Loader from "@components/shared/Loader/Loader";
import { AVAILIO_LOGO } from "@constants/assets";
import { STORAGE_KEYS } from "@constants/Kiosk";
import { kioskRouteEnum } from "@enums/route.enum";
import { useLandingPageData } from "@hooks/useLandingPageData";
import authSlice from "@slices/auth.slice";
import localSlice from "@slices/local.slice";
import { RootState } from "@store/index";
import commonUtils from "@utils/common.utils";
import { formatDateTime } from "@utils/kiosk.utils";

import SponsorImagesSlider from "../SponsorImagesSlider/SponsorImagesSlider";

const ScreenSaver: FC = () => {
  const { t } = useTranslation();
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [deviceId] = useLocalStorage(STORAGE_KEYS.KIOSK_DEVICE_ID, "");

  const selectedLanguage = useSelector(
    (state: RootState) => state.user.selectedLanguage
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: landingPageData, isLoading } = useLandingPageData(deviceId);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEYS.LANDING_REDIRECTED, "true");
    dispatch(
      authSlice.actions.passParams({ userEmail: "", verificationCode: "" })
    );
    dispatch(localSlice.actions.clearStorage(null));

    if (!deviceId) {
      navigate(kioskRouteEnum.Login);
      return;
    }
  }, []);

  const handleStart = (): void => {
    commonUtils.clearUserSession(false);
    navigate(kioskRouteEnum.Booking);
  };

  if (isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  const hasSponsorImages =
    landingPageData?.sponsorEnabled &&
    landingPageData.sponsorImages?.length > 0;

  return (
    <div className="user-select-none bg-light-subtle kiosk-container screensaver-container">
      {/* Header Section: Logo Left, Time Right */}
      <header className="screensaver-header d-flex justify-content-center align-items-center bg-transparent w-100">
        <div className="logo-wrapper overflow-hidden d-flex align-items-center">
          {landingPageData?.clublogoEnabled &&
            !!landingPageData.clublogoUrl && (
              <div className="screensaver-hero-image-wrapper overflow-hidden d-flex align-items-center justify-content-center hero-image-container">
                <img
                  src={landingPageData.clublogoUrl}
                  alt={t("kiosk.screensaver.clubLogoAlt")}
                  className="screensaver-logo object-fit-contain"
                />
              </div>
            )}
        </div>
        <div className="time-wrapper">
          <div className="screensaver-time text-dark mb-0 tabular-nums">
            {formatDateTime(currentTime, selectedLanguage)}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main
        className={`landing-page-video-bg ${!hasSponsorImages ? "video-full-height" : null}`}
      >
        <video className="landing-page-video" autoPlay muted loop playsInline>
          <source src="assets/video/kisok-landing-bg.mp4" type="video/mp4" />
        </video>
        <button
          onClick={handleStart}
          className="screensaver-button btn btn-primary rounded-pill fw-bold shadow-lg border-0 touch-button btn-pulse"
        >
          {t("kiosk.screensaver.touchToBook")}
        </button>
      </main>
      {hasSponsorImages && (
        <section className="landing-page-sponsor-wrapper">
          <SponsorImagesSlider sponsorImages={landingPageData.sponsorImages} />
        </section>
      )}
      {/* Footer & Sponsors */}
      <footer className="screensaver-footer w-100 d-flex flex-column align-items-center">
        <div className="d-flex flex-column align-items-center opacity-75">
          <img
            src={AVAILIO_LOGO}
            alt={t("kiosk.screensaver.fairgateLogoAlt")}
            className="screensaver-branding-logo"
          />
        </div>
      </footer>
    </div>
  );
};

export default ScreenSaver;
